import { formatDateTimeWithTimezone } from "../../../../../../utils/dateUtils";
import { OccurrenceComment } from "../../../../../../models/occurrence";
import { useIntl } from "react-intl";
import { OverlayTrigger, Tooltip } from "@rio-cloud/rio-uikit";
import { useState } from "react";

type OccurrenceCommentRowProps = {
  comment: OccurrenceComment;
  isAuthor: boolean;
  isEditing: boolean;
  editDraft: string;
  onEditDraftChange: (value: string) => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onDelete: () => void;
  savingEdit: boolean;
  deleting: boolean;
  disabled: boolean | undefined;
};

export const OccurrenceCommentRow = ({
  comment,
  isAuthor,
  isEditing,
  editDraft,
  onEditDraftChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  savingEdit,
  deleting,
  disabled,
}: OccurrenceCommentRowProps) => {
  const intl = useIntl();
  const [isAuthorHovered, setIsAuthorHovered] = useState(false);
  const [isAuthorClicked, setIsAuthorClicked] = useState(false);
  const authorName = comment.authorName ?? "—";
  const authorEmail = comment.authorEmail?.trim();
  const authorTooltipId = `occurrence-comment-author-${comment.uuid ?? comment.authorId ?? "unknown"}`;
  const showAuthorTooltip = isAuthorHovered || isAuthorClicked;
  const commentedAt = comment.commentedAt
    ? formatDateTimeWithTimezone(comment.commentedAt)
    : "";

  const dateLine = [commentedAt].filter(Boolean).join(" - ");

  return (
    <div className="pt-4 first:pt-0 my-6">
      <div className="flex flex-row items-start justify-between gap-2 mb-2">
        <span className="text-size-14 text-color-status-resting">
          {authorEmail ? (
            <OverlayTrigger
              trigger="click"
              placement="top"
              show={showAuthorTooltip}
              onToggle={(nextShow) => setIsAuthorClicked(nextShow)}
              overlay={
                <Tooltip id={authorTooltipId} allowOnTouch>
                  <div className="flex flex-col padding-10">
                    <span>{authorEmail}</span>
                  </div>
                </Tooltip>
              }
            >
              <span
                className="cursor-pointer"
                onMouseEnter={() => setIsAuthorHovered(true)}
                onMouseLeave={() => setIsAuthorHovered(false)}
              >
                {authorName}
              </span>
            </OverlayTrigger>
          ) : (
            <span>{authorName}</span>
          )}
          {dateLine ? ` - ${dateLine}` : ""}
        </span>
        {isAuthor && !isEditing && !disabled && (
          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              className="p-0 border-0 bg-transparent cursor-pointer"
              onClick={onStartEdit}
              aria-label="Editar comentário"
            >
              <span className="rioglyph rioglyph-pencil text-color-primary text-size-18" />
            </button>
            <button
              type="button"
              className="p-0 border-0 bg-transparent cursor-pointer disabled:opacity-50"
              onClick={onDelete}
              disabled={deleting}
              aria-label="Excluir comentário"
            >
              <span className="rioglyph rioglyph-trash text-color-gray text-size-18" />
            </button>
          </div>
        )}
      </div>

      {isEditing && !disabled ? (
        <div className="flex flex-col gap-3">
          <textarea
            className="form-control w-full min-h-[100px] resize-y mb-3"
            value={editDraft}
            onChange={(e) => onEditDraftChange(e.target.value)}
            disabled={savingEdit}
          />
          <div className="flex justify-end gap-8 items-center">
            <button
              type="button"
              className="btn btn-primary btn-link p-0"
              onClick={onCancelEdit}
              disabled={savingEdit}
            >
              {intl.formatMessage({ id: "occurrence.comments.cancel" })}
            </button>
            <button
              type="button"
              className="btn border border-[#00A0B0] text-[#00A0B0] bg-white hover:bg-[#F9FDFD] px-4 py-2 rounded disabled:opacity-50"
              onClick={onSaveEdit}
              disabled={savingEdit || !editDraft.trim()}
            >
              {intl.formatMessage({ id: "occurrence.comments.save" })}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-[#F9FAFB] rounded-md p-6 text-size-14 text-color-dark whitespace-pre-wrap break-words">
          {comment.comments ?? ""}
        </div>
      )}
    </div>
  );
};
